/**
 * Billing state and purchase actions, shared by the Home dashboard, the
 * BillingCard and the Billing page.
 *
 * Read state comes from the dashboard store (GET /api/tenant), so using it
 * costs no extra request. Paddle subscribers manage their plan through Paddle's
 * hosted portal.
 *
 * Every *purchase* lives here rather than in a view: checkout is only ever
 * offered on the Billing page, but the token-minting and completion-polling
 * rules must not be re-implemented per call site — getting them subtly
 * different is how a checkout ends up unbound to its tenant.
 */
import { ref, computed, onScopeDispose } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useDashboardStore } from '@/stores/dashboard'
import { usePaddle } from '@/composables/usePaddle'
import { usePlan, type BillingCycle } from '@lark-automadoc/shared-ui'
import {
  MAX_EXTRA_TEMPLATES_TOTAL,
  SEAT_TIERS,
  heldSeatQuantity,
  isFreshAdminRefusal,
  purchaseLanded,
  seatChangeNextStep,
  seatChangeRefusal,
  seatTierOptions,
  type BillingSnapshot,
  type BillingSummaryResponse,
  type PurchaseKind,
  type SeatChangeRefusal,
  type SeatPeriod,
  type SeatTier,
  type SeatTierOptions,
} from '@lark-automadoc/shared-types'

/**
 * Which confirmation to show once a purchase or change has gone through.
 *
 * Distinct from PurchaseKind (what the poll waits for): a seat *change* is not
 * a payment the customer just made in an overlay, and "Payment successful"
 * after they removed a seat would be wrong.
 */
export type PurchaseSuccess = 'pro' | 'templates' | 'seats' | 'seatsChanged' | 'pack'

/**
 * How each refusal from the seat-quantity route is worded. A switch with
 * literal keys rather than a lookup table, so the i18n audit can see them.
 */
function seatRefusalMessage(t: (key: string) => string, reason: SeatChangeRefusal): string {
  switch (reason) {
    case 'ambiguous': return t('billingSeatsAmbiguous')
    case 'ending': return t('billingSeatsEnding')
    case 'unavailable': return t('billingSeatsUnavailable')
    case 'blocked': return t('billingBlocked')
    case 'failed': return t('billingSeatsFailed')
  }
}

/**
 * Paddle price id per billing cycle. Build-time values, so this lives at module
 * scope rather than being rebuilt on every useBilling() call.
 *
 * `quarterly` is optional per environment: blank until the price exists in that
 * Paddle catalog, and while it is blank the cycle is hidden rather than offered
 * as a checkout that cannot open.
 */
const PRO_PRICE_IDS: Record<BillingCycle, string | undefined> = {
  monthly: import.meta.env.VITE_PADDLE_PRICE_PRO_MONTHLY,
  quarterly: import.meta.env.VITE_PADDLE_PRICE_PRO_QUARTERLY,
  yearly: import.meta.env.VITE_PADDLE_PRICE_PRO_ANNUAL,
}

export function useBilling() {
  const { t } = useI18n()
  const dashboard = useDashboardStore()
  const { openCheckout, preload: preloadPaddle } = usePaddle()
  const { tenant: currentTenant, planStatus, usageStatus, isAppAdmin, packs } = storeToRefs(dashboard)
  const { planDisplay, isPro, isTrial, canStartTrial, isProExpired } = usePlan({ planStatus, usageStatus })

  // Purchased extras apply on every plan, including Free — they are separate
  // subscriptions, not Pro features.
  const extraTemplateCount = computed(() => currentTenant.value?.extra_template_count || 0)
  // An operator's concession is not a purchase: shown as "granted", the rest as
  // "purchased". An API that predates the split reports none granted.
  const extraTemplatesGranted = computed(() =>
    Math.min(extraTemplateCount.value, currentTenant.value?.extra_templates_granted || 0),
  )
  const extraTemplatesPurchased = computed(() => extraTemplateCount.value - extraTemplatesGranted.value)
  // Exact, not an estimate: a pack is decremented as each document completes.
  const packBalance = computed(() => packs.value?.balance ?? usageStatus.value?.packBalance ?? 0)
  const packExpiresAt = computed(() => packs.value?.nearestExpiry ?? usageStatus.value?.packExpiresAt ?? null)
  /** Seats in force per tier. On the tenant, so any member can read it. */
  const seatsInForce = computed(() => currentTenant.value?.seats ?? { starter: 0, business: 0 })
  const billingPeriod = computed(() => currentTenant.value?.billing_period || null)
  const planExpiresAt = computed(() => planStatus.value?.expiresAt || null)
  // The *enforced* allowance, not the stored cap: getUsageStatus already drops
  // this to the Free tier once the plan lapses, so the UI matches what the
  // server will actually permit.
  const maxTemplates = computed(() => usageStatus.value?.maxTemplates || 0)
  // Templates parked above the cap. Shown here because this is the page where a
  // customer decides whether to pay — "5 locked" is the concrete thing an
  // upgrade buys back, and nothing else in the portal mentions them.
  const lockedTemplates = computed(() => usageStatus.value?.lockedTemplates || 0)
  /**
   * Has any Paddle relationship at all — keyed on the *customer* id.
   *
   * `tenants.paddle_customer_id` is the one billing column the Paddle webhook
   * still writes (applyGrantEffects), for a subscription of any kind; it is
   * not entitlement but how the hosted portal is opened, and
   * the portal session needs nothing else. Add-ons and seats are sold on every
   * plan including Free, so anything keyed on Pro would leave such a tenant
   * with no Manage button — billed monthly with no self-serve way to cancel.
   */
  const isPaddleSubscriber = computed(() => !!currentTenant.value?.paddle_customer_id)

  /**
   * A refused billing call, in words: requireFreshAdmin's FRESH_TOKEN_REQUIRED means the
   * person must sign in again for the API to re-confirm they are an admin.
   */
  function refusalText(error: unknown, fallback: string): string {
    return isFreshAdminRefusal(error) ? t('adminSignInAgain') : t(fallback)
  }

  function formatDate(ts: number | null): string {
    return ts ? new Date(ts).toLocaleDateString() : ''
  }

  /** Billing period and/or access end date, joined for the card subtitle. */
  const billingSubtitle = computed(() => {
    const parts: string[] = []
    if (billingPeriod.value === 'monthly') parts.push(t('billingPeriodMonthly'))
    else if (billingPeriod.value === 'quarterly') parts.push(t('billingPeriodQuarterly'))
    else if (billingPeriod.value === 'annual') parts.push(t('billingPeriodAnnual'))
    if (isPro.value && planExpiresAt.value) {
      parts.push(t('billingValidUntil', { date: formatDate(planExpiresAt.value) }))
    }
    return parts.join(' · ')
  })

  const billingLoading = ref(false)
  const billingError = ref('')

  /** Open Paddle's hosted customer portal (card, invoices, cancellation). */
  async function manageBilling(): Promise<void> {
    if (billingLoading.value) return
    // The route is behind appAdminMiddleware — say why rather than 403ing into
    // a generic failure. The server gate remains the real boundary.
    if (!isAppAdmin.value) {
      billingError.value = t('billingAdminOnly')
      return
    }
    billingLoading.value = true
    billingError.value = ''
    try {
      const { tenantApi } = await import('@/services/api')
      const { url } = await tenantApi.createBillingPortalSession()
      window.open(url, '_blank', 'noopener')
    } catch (error) {
      console.error('Failed to open billing portal:', error)
      billingError.value = refusalText(error, 'billingManageError')
    } finally {
      billingLoading.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Purchases
  // ---------------------------------------------------------------------------

  /**
   * Set by the Paddle webhook when a chargeback lands. Purchase blocks hide,
   * but the Manage-subscription button deliberately stays: a blocked tenant
   * must still be able to see invoices and cancel what they already hold.
   *
   * This is presentation only — the server refuses to mint a checkout token
   * for a blocked tenant, and that is what actually stops a charge.
   */
  const isBillingBlocked = computed(() => currentTenant.value?.billing_blocked === 1)

  /**
   * Whether this build has a Pro catalog wired up.
   *
   * These are build-time values, so they are constants at runtime — an env with
   * an unpopulated catalog (see .env.production) must not render enabled buy
   * buttons that can only fail. Views use these to hide the purchase blocks
   * entirely rather than offering a dead funnel; requirePriceId below is the
   * belt-and-braces guard for anything that still gets called.
   */
  const canBuyPro = computed(
    () =>
      !isBillingBlocked.value &&
      !!import.meta.env.VITE_PADDLE_CLIENT_TOKEN &&
      !!PRO_PRICE_IDS.yearly &&
      !!PRO_PRICE_IDS.monthly,
  )
  /**
   * Quarterly is gated separately, not folded into canBuyPro.
   *
   * The price has to exist in each environment's Paddle catalog, and until it
   * does the cycle should just be absent — requiring it in canBuyPro would take
   * monthly and annual down with it and close the whole funnel.
   */
  const canBuyProQuarterly = computed(() => canBuyPro.value && !!PRO_PRICE_IDS.quarterly)
  const canBuyExtraTemplates = computed(
    () =>
      !isBillingBlocked.value &&
      !!import.meta.env.VITE_PADDLE_CLIENT_TOKEN &&
      !!import.meta.env.VITE_PADDLE_PRICE_EXTRA_TEMPLATE,
  )

  // ---------------------------------------------------------------------------
  // Seats and packs: what is on sale comes from the API, not from this build
  // ---------------------------------------------------------------------------

  /**
   * GET /api/tenant/billing/summary — seats, packs, the automation allowance,
   * and the price ids this environment sells.
   *
   * The price ids come from the API rather than VITE_ variables on purpose:
   * they are the ids the webhook classifies a payment against, so a checkout
   * opened with them is always one the webhook recognises. A portal with its
   * own copy could sell something the webhook then ignored. app_admin only.
   * Billing loads it to sell; Home loads it only when the company holds seats,
   * to say who has them. Pricing never does.
   */
  const summary = ref<BillingSummaryResponse | null>(null)
  const summaryError = ref(false)

  /**
   * Callers that overlap (Home's Refresh and its watcher) share one request.
   * `fresh` skips a request already running: the seat check and the post-purchase
   * poll need state from after they were asked, not from a request that started
   * before.
   */
  let summaryInFlight: Promise<void> | null = null
  /** Bumped per request: an older response that lands late must not overwrite a fresh one. */
  let summarySeq = 0

  function loadSummary(options: { fresh?: boolean } = {}): Promise<void> {
    if (!isAppAdmin.value) return Promise.resolve()
    if (summaryInFlight && !options.fresh) return summaryInFlight
    const request: Promise<void> = fetchSummary().finally(() => {
      // A fresh request may have replaced this one meanwhile; leave it be.
      if (summaryInFlight === request) summaryInFlight = null
    })
    summaryInFlight = request
    return request
  }

  async function fetchSummary(): Promise<void> {
    const seq = ++summarySeq
    try {
      const { tenantApi } = await import('@/services/api')
      const result = await tenantApi.getBillingSummary()
      if (seq !== summarySeq) return
      summary.value = result
      summaryError.value = false
    } catch (error) {
      console.error('[Billing] Failed to load billing summary:', error)
      if (seq === summarySeq) summaryError.value = true
    }
  }

  /** Paddle.js cannot open a checkout without this environment's client token. */
  const hasPaddleClient = !!import.meta.env.VITE_PADDLE_CLIENT_TOKEN

  /** Seat tiers to render, each with only the periods that are sold or held. */
  const seatOptions = computed<SeatTierOptions[]>(() => {
    const s = summary.value
    if (!s || !hasPaddleClient) return []
    return SEAT_TIERS.map((tier) => seatTierOptions(s, tier)).filter(
      (o) => o.periods.length > 0 || o.fixedGrants.length > 0,
    )
  })

  /** Pack sizes on sale here. An unavailable one is left out, never shown disabled. */
  const packOffers = computed(() => {
    const s = summary.value
    if (!s || s.billingBlocked || !hasPaddleClient) return []
    return s.products.packs.flatMap((p) => (p.available && p.priceId ? [{ size: p.size, priceId: p.priceId }] : []))
  })

  const purchaseError = ref('')
  /** Non-null while the post-purchase confirmation dialog is open. */
  const purchaseSuccess = ref<PurchaseSuccess | null>(null)
  const checkoutLoading = ref(false)
  /** `tier:period` of the seat purchase or change in progress, so only that button spins. */
  const seatsLoading = ref<string | null>(null)
  /** Size of the pack whose checkout is opening. */
  const packLoading = ref<number | null>(null)
  const extraTemplatesLoading = ref(false)
  const startingTrial = ref(false)

  /**
   * Called when a Paddle checkout completes. The entitlement is applied by the
   * Paddle webhook *asynchronously*, so the dashboard won't reflect it
   * instantly — confirm immediately, then poll so the page catches up.
   *
   * The poll stops as soon as the purchase actually shows up rather than
   * running a fixed number of times: the webhook usually lands within a second
   * or two, so a blind six-refresh chain spent five requests confirming
   * something already known. Intervals widen as we go, because if it has not
   * arrived quickly it is a slow webhook, not a fast one we keep missing.
   */
  let pollTimer: ReturnType<typeof setTimeout> | null = null

  /** Backoff schedule; length also caps the number of attempts. */
  const POLL_DELAYS_MS = [1500, 2500, 4000, 6000]

  /** The numbers a purchase moves. What "moved" means per kind is purchaseLanded() in shared-types. */
  function snapshot(): BillingSnapshot {
    return {
      trial: isTrial.value,
      expiresAt: planExpiresAt.value,
      extraTemplates: extraTemplateCount.value,
      seats: { ...seatsInForce.value },
      packBalance: packBalance.value,
    }
  }

  /**
   * A seat purchase or change whose webhook is still awaited. Views disable Buy
   * seats while it is set: until the webhook lands the API still answers
   * needsCheckout, so a second click would open — and charge — a second
   * subscription for the same seats. Only seats are tracked; no other product
   * has that failure.
   */
  const awaitingPurchase = ref<'seats' | null>(null)

  function handleCheckoutComplete(kind: PurchaseKind, success: PurchaseSuccess = kind): void {
    purchaseSuccess.value = success
    // Only seats are guarded, and only a seat poll that lands releases it: a
    // pack bought meanwhile cancels the seat poll below, and must not re-enable
    // Buy seats while that webhook is still outstanding.
    if (kind === 'seats') awaitingPurchase.value = 'seats'
    // A second purchase before the first chain finishes would otherwise leave
    // two chains refreshing in parallel.
    if (pollTimer) clearTimeout(pollTimer)

    const before = snapshot()

    let attempt = 0
    const poll = async (): Promise<void> => {
      await dashboard.refresh()
      const landed = purchaseLanded(kind, before, snapshot())
      if (landed || attempt >= POLL_DELAYS_MS.length) {
        pollTimer = null
        // Once, at the end, rather than on every attempt: the summary is the
        // Billing page's detail view, and refetching it each time doubled the
        // poll's requests.
        if (summary.value) await loadSummary({ fresh: true })
        // Kept when the webhook never showed up: the page would read "not
        // bought" and offer the same checkout again. A reload clears it.
        if (landed && kind === 'seats') awaitingPurchase.value = null
        return
      }
      pollTimer = setTimeout(() => void poll(), POLL_DELAYS_MS[attempt++])
    }
    void poll()
  }

  // Navigating away mid-chain would otherwise keep refreshing the store for
  // another ~10s against an unmounted view.
  onScopeDispose(() => {
    if (pollTimer) clearTimeout(pollTimer)
  })

  /**
   * Mint the signed token that binds this checkout to our tenant.
   *
   * custom_data is set in the browser, so the webhook cannot trust a raw tenant
   * key from here — it accepts only a token the API minted for our
   * authenticated session. Returns null (and sets purchaseError) on failure.
   */
  async function requireCheckoutToken(context: string): Promise<string | null> {
    purchaseError.value = ''
    // The billing routes are behind appAdminMiddleware — say why rather than
    // rendering a button that 403s into a generic failure.
    if (!isAppAdmin.value) {
      purchaseError.value = t('billingAdminOnly')
      return null
    }
    try {
      const { tenantApi } = await import('@/services/api')
      const { token } = await tenantApi.createCheckoutToken()
      return token
    } catch (error) {
      console.error(`[Billing] Cannot open ${context}: failed to mint checkout token`, error)
      purchaseError.value = refusalText(error, 'billingManageError')
      return null
    }
  }

  /**
   * Guard against an unconfigured environment.
   *
   * The VITE_PADDLE_* vars are per-env build-time values; if an env file is
   * missing them the build still succeeds and `priceId` arrives as undefined.
   * Paddle would then open an overlay that fails inside a cross-origin iframe,
   * where the error is invisible. Fail here instead, loudly in the console.
   */
  function requirePriceId(priceId: string | undefined, name: string): priceId is string {
    if (priceId) return true
    console.error(`[Billing] ${name} is not configured for this environment (missing VITE_PADDLE_* price id)`)
    purchaseError.value = t('billingManageError')
    return false
  }

  /** Subscribe to / renew Pro on the given billing cycle. */
  async function startCheckout(cycle: BillingCycle): Promise<void> {
    if (checkoutLoading.value) return
    // Claimed before the token round-trip, not after: minting the token and
    // loading Paddle.js both happen before the overlay appears, and without a
    // loading state that whole window looks like a dead button.
    checkoutLoading.value = true
    try {
      const checkoutToken = await requireCheckoutToken('Pro checkout')
      if (!checkoutToken) return

      const proPriceId = PRO_PRICE_IDS[cycle]
      if (!requirePriceId(proPriceId, `Pro ${cycle} price`)) return

      await openCheckout(
        [{ priceId: proPriceId, quantity: 1 }],
        { checkout_token: checkoutToken },
        { onComplete: () => handleCheckoutComplete('pro') },
      )
    } catch (error) {
      console.error('[Billing] Failed to open Pro checkout:', error)
      purchaseError.value = refusalText(error, 'billingManageError')
    } finally {
      checkoutLoading.value = false
    }
  }

  /**
   * Buy the per-unit Extra Template add-on.
   *
   * If the tenant already has an add-on subscription, the backend raises its
   * quantity in place (one subscription, no duplicate). Only the *first*
   * purchase opens a Paddle checkout — the backend signals that with
   * `needsCheckout`.
   */
  async function buyExtraTemplates(quantity: number): Promise<void> {
    if (extraTemplatesLoading.value || quantity < 1) return

    // Pre-check the ceiling the server enforces. Without this the only feedback
    // is the worker's untranslated English 400, which the UI must not show.
    // The server remains the real boundary; this is purely for a usable message.
    if (extraTemplateCount.value + quantity > MAX_EXTRA_TEMPLATES_TOTAL) {
      purchaseError.value = t('billingExtraTemplatesCap', { max: MAX_EXTRA_TEMPLATES_TOTAL })
      return
    }

    extraTemplatesLoading.value = true
    try {
      const checkoutToken = await requireCheckoutToken('extra-templates purchase')
      if (!checkoutToken) return

      const { tenantApi } = await import('@/services/api')
      const res = await tenantApi.addExtraTemplates(quantity)
      if (res.needsCheckout) {
        // First add-on purchase — a subscription must be created via checkout.
        // The price id is only needed on this branch: raising the quantity of
        // an existing subscription is done server-side from env.
        if (!requirePriceId(import.meta.env.VITE_PADDLE_PRICE_EXTRA_TEMPLATE, 'Extra Template price')) return
        await openCheckout(
          [{ priceId: import.meta.env.VITE_PADDLE_PRICE_EXTRA_TEMPLATE, quantity }],
          { checkout_token: checkoutToken },
          { onComplete: () => handleCheckoutComplete('templates') },
        )
      } else {
        // Quantity raised on the existing subscription — webhook recompute follows.
        handleCheckoutComplete('templates')
      }
    } catch (error) {
      console.error('[Billing] Failed to add extra templates:', error)
      purchaseError.value = refusalText(error, 'billingManageError')
    } finally {
      extraTemplatesLoading.value = false
    }
  }

  /**
   * Buy seats, or change how many the tenant has, on one tier and period.
   *
   * `quantity` is the new total for that subscription. The server answers
   * `needsCheckout` when there is no subscription for the tier and period yet,
   * and a Paddle checkout then creates one with that many seats; otherwise it
   * has already changed the quantity, prorated at once, and the webhook writes
   * the grant. Asking the server first rather than trusting the summary means a
   * stale page cannot open a second subscription for seats already held.
   *
   * The checkout token is minted only on the checkout branch. The API refuses
   * to mint one for a billing-blocked tenant, and a blocked tenant must still be
   * able to *reduce* their seats — minting up front would have closed that.
   *
   * A change spends money with no Paddle overlay, so the view confirms it
   * before calling this (Billing.vue).
   *
   * @param expected - seats held on this tier and period when the person chose
   *   `quantity` (null for a first purchase). Re-checked against a fresh summary
   *   first: the route changes an existing subscription with no overlay, so a
   *   page gone stale in another tab would otherwise turn "buy 1" into an
   *   unconfirmed reduction. See heldSeatQuantity().
   */
  async function setSeats(tier: SeatTier, period: SeatPeriod, quantity: number, expected: number | null): Promise<void> {
    if (seatsLoading.value) return
    purchaseError.value = ''
    // The route is app_admin-only; say so rather than 403 into a generic failure.
    if (!isAppAdmin.value) {
      purchaseError.value = t('billingAdminOnly')
      return
    }
    seatsLoading.value = `${tier}:${period}`
    try {
      await loadSummary({ fresh: true })
      if (!summary.value || summaryError.value) {
        purchaseError.value = t('billingSeatsFailed')
        return
      }
      if (heldSeatQuantity(summary.value, tier, period) !== expected) {
        // The page now shows the fresh numbers; let the person choose again.
        purchaseError.value = t('billingSeatsStale')
        return
      }

      const { tenantApi } = await import('@/services/api')
      let res
      try {
        res = await tenantApi.updateSeats({ tier, quantity, billingPeriod: period })
      } catch (error) {
        console.error('[Billing] Seat change refused:', error)
        purchaseError.value = isFreshAdminRefusal(error) ? t('adminSignInAgain') : seatRefusalMessage(t, seatChangeRefusal(error))
        return
      }

      const next = seatChangeNextStep(res)
      if (next === 'changed') {
        handleCheckoutComplete('seats', 'seatsChanged')
        return
      }
      if (next === 'failed') {
        purchaseError.value = t('billingSeatsFailed')
        return
      }

      const priceId = summary.value?.products.seats[tier][period].priceId ?? undefined
      if (!requirePriceId(priceId, `${tier} ${period} seat price`)) return
      const checkoutToken = await requireCheckoutToken('seat checkout')
      if (!checkoutToken) return
      await openCheckout(
        [{ priceId, quantity }],
        { checkout_token: checkoutToken },
        { onComplete: () => handleCheckoutComplete('seats') },
      )
    } catch (error) {
      console.error('[Billing] Failed to buy or change seats:', error)
      purchaseError.value = refusalText(error, 'billingManageError')
    } finally {
      seatsLoading.value = null
    }
  }

  /** Buy one document pack of the given size — a one-time Paddle checkout. */
  async function buyPack(size: number): Promise<void> {
    if (packLoading.value !== null) return
    packLoading.value = size
    try {
      const checkoutToken = await requireCheckoutToken('pack checkout')
      if (!checkoutToken) return
      const priceId = packOffers.value.find((p) => p.size === size)?.priceId
      if (!requirePriceId(priceId, `${size}-document pack price`)) return

      await openCheckout(
        [{ priceId, quantity: 1 }],
        { checkout_token: checkoutToken },
        { onComplete: () => handleCheckoutComplete('pack') },
      )
    } catch (error) {
      console.error('[Billing] Failed to open pack checkout:', error)
      purchaseError.value = refusalText(error, 'billingManageError')
    } finally {
      packLoading.value = null
    }
  }

  /**
   * Start the in-app trial. Costs nothing and takes no card, so it needs no
   * Paddle checkout — and deliberately no app_admin gate: POST
   * /api/tenant/start-trial is JWT-only server-side (unlike the billing routes,
   * which sit behind appAdminMiddleware). Adding a client-side admin check here
   * would block non-admins from a path the server allows, on the main
   * conversion path.
   */
  async function startTrial(): Promise<void> {
    if (startingTrial.value) return
    purchaseError.value = ''
    startingTrial.value = true
    try {
      const { tenantApi } = await import('@/services/api')
      await tenantApi.startTrial()
      await dashboard.refresh()
    } catch (error) {
      console.error('[Billing] Failed to start trial:', error)
      purchaseError.value = refusalText(error, 'billingManageError')
    } finally {
      startingTrial.value = false
    }
  }

  return {
    currentTenant,
    planDisplay,
    isPro,
    isTrial,
    canStartTrial,
    isProExpired,
    extraTemplateCount,
    extraTemplatesGranted,
    extraTemplatesPurchased,
    packBalance,
    packExpiresAt,
    seatsInForce,
    maxTemplates,
    lockedTemplates,
    isPaddleSubscriber,
    isAppAdmin,
    billingSubtitle,
    billingLoading,
    billingError,
    formatDate,
    manageBilling,
    // Purchases
    isBillingBlocked,
    canBuyPro,
    canBuyProQuarterly,
    canBuyExtraTemplates,
    purchaseError,
    purchaseSuccess,
    checkoutLoading,
    extraTemplatesLoading,
    startingTrial,
    preloadPaddle,
    startCheckout,
    buyExtraTemplates,
    startTrial,
    // Seats and packs
    summary,
    summaryError,
    loadSummary,
    seatOptions,
    packOffers,
    seatsLoading,
    awaitingPurchase,
    packLoading,
    setSeats,
    buyPack,
  }
}
